import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, IconButton, Typography, Skeleton } from '@mui/material';
import { Button } from '@mui/material';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
// import './App.css';
import Map from '../components/map-container.js';
import TimeSlider from '../components/sliders/slider.js';
import TimeSliderVisibility from '../components/sliders/slider_visibility.js';
import TimeSliderNowcasting from '../components/sliders/slider_nowcasting.js';
import TimeSliderDust from '../components/sliders/slider_dust.js';
import VerticalSlider from '../components/sliders/slider_vertical.js';
import ForecastRelease from '../components/layers_selector/forecast_release.js';
import ProbabilitySelector from '../components/layers_selector/probability-selector.js';
import RiskSelector from '../components/layers_selector/risk-selector.js';
import MapLayers from '../components/layers_selector/map-layers.js';

import LocationButton from '../components/map-controls/location-button';
import FullScreenButton from '../components/map-controls/fullscreen-button';
import { ZoomInButton, ZoomOutButton } from '../components/map-controls/zoom-button';
import CenterFocusButton from '../components/map-controls/centerfocus-button';
import { useNavigate } from 'react-router-dom';
import Badge from '@mui/material/Badge';
import useMediaQuery from '@mui/material/useMediaQuery';

function ViewMap() {
  const [layerOpacity, setLayerOpacity] = useState(0.69);
  const [showIcons, setShowIcons] = useState(false);
  const [showVerticalSlider, setShowVerticalSlider] = useState(false);

  //SLIDER
  const [selectedTime, setSelectedTime] = useState(null); // Estado para manejar el tiempo seleccionado
  const [selectedCategory, setSelectedCategory] = useState(['Surface', 'Above ∞']); // Valores iniciales para el rango completo

  // Pre-calculate starting date and initial filters for immediate load
  const initialDate = (() => {
    const current = new Date();
    current.setMinutes(0, 0, 0, 0);
    return current.toISOString();
  })();

  const getFilterForDate = (dateStr) => {
    return `VALID_TIMESTAMP = '${dateStr}' AND (TOP >= 0 OR TOP IS NULL) AND ((CONVECTIVE_MATRIX_RISK = 1) OR (CONVECTIVE_MATRIX_RISK = 2) OR (CONVECTIVE_MATRIX_RISK = 3) OR (CONVECTIVE_MATRIX_RISK = 4) OR (CONVECTIVE_MATRIX_RISK = 5))`.replace(/\s+/g, ' ').trim();
  };

  const [cqlFilter, setCqlFilter] = useState(getFilterForDate(initialDate));
  const [cqlFilter2, setCqlFilter2] = useState("LOWER_VAL <= 1000000 AND UPPER_VAL > 0");

  //Manejo de capas
  const [activeLayer, setActiveLayer] = useState('CNN:EU_025_live_demo');
  const [activeLayerName, setActiveLayerName] = useState('CNN');

  //forecast release
  const [forecastValue, setForecastValue] = useState(null);

  const mapRef = useRef();

  const navigate = useNavigate();
  //obten el valorde forcastrelease al iniciar la webapp
  useEffect(() => {
    const controller = new AbortController();

    const initialDate = getStartingDate(); // Esta función necesita devolver una fecha en formato UTC
    const dateObj = new Date(initialDate);
    // Ajusta la fecha en UTC
    dateObj.setUTCDate(dateObj.getUTCDate() - 1); // Resta 1 día en UTC
    // Formatea la fecha ajustada como ISO string (en UTC) para pasarlo a ForecastRelease
    const adjustedDate = dateObj.toISOString();
    // Llama a ForecastRelease con la capa activa y el valor de fecha ajustado
    ForecastRelease(adjustedDate, setForecastValue, activeLayer, controller.signal);

    return () => {
      controller.abort();
    };
  }, [activeLayer]); // Ejecuta cuando cambia la capa activa

  // Cuando una capa PMTiles devuelve su hora inicial, ajusta el slider
  const handleInitialTimeChange = useCallback((isoDate) => {
    setForecastValue(isoDate);
    // También actualizar selectedDateTime para que el cqlFilter arranque desde ahí
    setSelectedDateTime(isoDate);
  }, []);

  //formateo del forecast release
  function formatForecastDate(isoDate) {
    if (!isoDate) return '';

    // Divide la cadena ISO (que está en UTC) en fecha y hora
    const [date, time] = isoDate.split('T');

    // Extrae la hora y minutos, considerando que isoDate ya está en UTC
    // No necesitas manipular la zona horaria, dado que isoDate debe ser una cadena UTC
    const hourMinute = time.substring(0, 5); // Obtiene solo la hora y minutos

    // Combina la fecha y hora en el nuevo formato y agrega 'UTC' para claridad
    return `${date} ${hourMinute} UTC`;
  }

  const categoryMap = {
    'Surface': 0,
    'Above FL25': 25,
    'Above FL50': 50,
    'Above FL75': 75,
    'Above FL100': 100,
    'Above FL125': 125,
    'Above FL150': 150,
    'Above FL175': 175,
    'Above FL200': 200,
    'Above FL225': 225,
    'Above FL250': 250,
    'Above FL275': 275,
    'Above FL300': 300,
    'Above FL325': 325,
    'Above FL350': 350,
    'Above FL375': 375,
    'Above FL400': 400,
    'Above FL425': 425,
    'Above FL450': 450,
    'Above ∞': 10000
  };

  const getStartingDate = () => {
    const current = new Date();
    const adjustedDateTime = new Date(
      current.getFullYear(),
      current.getMonth(),
      current.getDate(),
      current.getHours(),
      0,
      0,
      0
    );
    // Falta un return aquí
    return adjustedDateTime.toISOString();  // Deberías retornar el objeto Date o una cadena ISO
  };

  const [selectedDateTime, setSelectedDateTime] = useState(getStartingDate());

  const fetchGeoJSON = async (timestamp) => {
    const [lowerKey, upperKey] = selectedCategory;

    const lowerValue = categoryMap[lowerKey] ?? 0;
    const upperValueRaw = categoryMap[upperKey] ?? 10000;
    const upperValue = upperValueRaw === Infinity ? 10000 : upperValueRaw; // por si acaso

    // Si quieres incluir "sin TOP" usa OR TOP IS NULL para el límite inferior.
    // Para el superior, solo aplica si no es infinito.
    const upperClause = upperValue >= 10000 ? '' : ` AND TOP <= ${upperValue}`;

    const newCqlFilter = `
      VALID_TIMESTAMP = '${timestamp}'
      AND (TOP >= ${lowerValue} OR TOP IS NULL)
      ${upperClause}
      AND (
        (CONVECTIVE_MATRIX_RISK = 1) OR
        (CONVECTIVE_MATRIX_RISK = 2) OR
        (CONVECTIVE_MATRIX_RISK = 3) OR
        (CONVECTIVE_MATRIX_RISK = 4) OR
        (CONVECTIVE_MATRIX_RISK = 5)
      )`.replace(/\s+/g, ' ').trim();

    // Si tu backend usa hgts*100, mantén el *100. Para infinito vale 10000*100.
    const newCqlFilter2 = `LOWER_VAL <= ${upperValue * 100} AND UPPER_VAL > ${lowerValue * 100}`;

    setCqlFilter(newCqlFilter);
    setCqlFilter2(newCqlFilter2);
  };


  const onTimeChange = (newDateTime) => {
    setSelectedDateTime(newDateTime); // Actualiza el estado con el nuevo timestamp
    // setSelectedCategory(categoryName);
    fetchGeoJSON(newDateTime, selectedCategory); // Llama a fetchGeoJSON con el nuevo timestamp y categoría actual
  };

  useEffect(() => {
    fetchGeoJSON(selectedDateTime, selectedCategory);//, selectedCategory);
  }, [selectedDateTime, selectedCategory]); // [selectedDateTime, selectedCategory]); //


  //PROBABILITY SELECTOR
  const [selectedProbability, setSelectedProbability] = useState('FP 0.03');

  const handleProbabilityLevelClick = (newProbability) => {
    setSelectedProbability(newProbability);
  };

  // Mostrar componentes en función de la capa activa
  const handleLayerChange = (layerName) => {
    setActiveLayer(layerName); // Actualizar la capa activa

    // Actualizar el selector de riesgo y mostrar/ocultar componentes según la capa activa
    switch (true) {
      case (layerName === 'KAIROS:EU_025_live_demo' || layerName === 'KAIROS:EU_0125_live_demo'):
        setActiveLayerName('Convection');
        break;
      case (layerName === 'CNN:EU_025_live_demo' || layerName === 'CNN:EU_01_live_demo' || layerName === 'CNN:euro_1k_00625_15m'):
        setActiveLayerName('CNN');
        break;
      case layerName === 'VISIBILITY:visibility_live_demo':
        setActiveLayerName('Visibility');
        break;
      case layerName === 'NOWCASTING:nowcastnet_live_demo':
        setActiveLayerName('Nowcasting');
        break;
      case layerName === 'ICE:ICE_025_live_demo':
        setActiveLayerName('Ice');
        break;
      case layerName === 'TURBULENCE:TURBULENCE_0125_live_demo':
        setActiveLayerName('Turbulence');
        break;
      case layerName === 'DUST:DUST_025_live_demo':
        setActiveLayerName('Dust');
        break;
      default:
        setActiveLayerName('Convection');
        break;
    }
  };

  const [isFullscreen, setIsFullscreen] = useState(false);
  const mapContainerRef = useRef(null);

  // Función para alternar entre pantalla completa y pantalla normal
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      mapContainerRef.current.requestFullscreen().catch((err) => {
        console.error(`Error al intentar habilitar el modo de pantalla completa: ${err.message}`);
      });
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Efecto para asegurar que el mapa ajusta su tamaño después de cambiar el modo de pantalla completa
  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        setIsFullscreen(false);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);


  // Para que la capa se actualice automáticamente al cambiar la capa activa
  useEffect(() => {
    // Se llama a fetchGeoJSON usando el valor actual del slider cada vez que activeLayer cambia
    fetchGeoJSON(selectedDateTime);
  }, [activeLayer, selectedDateTime]);

  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const ICON_ONLY_BREAKPOINT = 1100;          // igual que MapLayers
  const isIconOnly = windowWidth <= ICON_ONLY_BREAKPOINT;
  const isCompact = useMediaQuery('(max-width:600px)');
  const showText = !isIconOnly && !isCompact; // igual que MapLayers
  const isCNNLayer = activeLayerName === 'CNN';

  return (
    <div className="App" style={{ position: 'relative' }}>
      <Box
        sx={{
          position: 'absolute',
          top: { xs: 100, sm: 180, md: 90 },
          left: 20,
          zIndex: 11000,
          pointerEvents: 'none',
          display: { xs: 'none', sm: 'block' },
        }}
      >
        {/* Envolvemos el contenido con el Badge */}
        <Badge
          badgeContent="ISSUE TIME" // Texto o número del badge
          color="error"      // Color del tema
          sx={{
            '& .MuiBadge-badge': {
              right: -10,
              top: -5,
              fontSize: '0.65rem',
              fontWeight: 'bold'
              // pointerEvents: 'auto' // Descomenta si quieres que el badge sea clickeable
            }
          }}
        >
          <Box
            component="span"
            className="MuiSlider-valueLabelLabel"
            sx={{
              backgroundColor: '#b5e5d37c',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              padding: '8px 14px',
              borderRadius: '8px',
              fontFamily: "'Inter', monospace",
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
              display: 'inline-block',
              color: '#000000',
            }}
          >
            <Typography sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>
              {forecastValue // <--- Cambia selectedDateTime por forecastValue
                ? formatForecastDate(forecastValue)
                : <Skeleton variant="text" width={120} animation="pulse" sx={{ display: 'inline-block' }} />
              }
            </Typography>
          </Box>
        </Badge>
      </Box>
      <div ref={mapContainerRef} style={{ height: '100vh', width: '100%' }}>
        <Map
          onLayerChange={handleLayerChange}
          selectedCategory={selectedCategory}
          selectedProbability={selectedProbability}
          cqlFilter={cqlFilter}
          cqlFilter2={cqlFilter2}
          mapRef={mapRef}
          onInitialTimeChange={handleInitialTimeChange}
        />
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '60px',
          backgroundColor: '#161616',
          zIndex: 1000,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0 20px',
          boxSizing: 'border-box',
          overflow: 'hidden'
        }}>
          <a href="https://www.aimethods.com/" target="_blank" rel="noopener noreferrer" style={{ height: '80%' }}>
            <img
              src="/images/AIMethods_logo.png"
              alt="AI Methods"
              style={{
                height: '100%',
                objectFit: 'contain',
                filter: 'brightness(0) invert(1)'
              }}
            />
          </a>

          <a href="https://www.sesarju.eu/" target="_blank" rel="noopener noreferrer"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              flexShrink: 0,
              maxWidth: 'clamp(120px, 30vw, 240px)',
              height: 'auto',
            }}
          >
            <img
              src="/images/KAIROS-supportedby_white.svg"
              alt="KAIROS supported by"
              style={{
                width: '100%',
                height: 'auto',
                maxHeight: '90px',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          </a>
        </div>
        <div>
          {/* SLIDER vertical */}
          {showVerticalSlider && (activeLayerName === 'Convection' || activeLayerName === 'CNN') && (
            <VerticalSlider
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              selectedDateTime={selectedDateTime}
              fetchGeoJSON={fetchGeoJSON}
            />
          )}


          {/* <RiskSelector/> */}

          {/* <MapLayers
          activeMapBaseLayer={activeMapBaseLayer}
          layerOpacity={layerOpacity}
          cqlFilter={cqlFilter}
          cqlFilter2={cqlFilter2}
          selectedDateTime={selectedDateTime}
        /> */}
          {/* 
<ProbabilitySelector
          selectedProbability={selectedProbability}
          onProbabilityChange={handleProbabilityLevelClick}
        /> */}

        </div>
      </div>

      <Box sx={{
        position: 'absolute',
        top: { xs: 'auto', md: '135px' },
        bottom: { xs: showIcons ? '85px' : '60px', md: 'auto' },
        left: { xs: '50%', md: 'auto' },
        right: { xs: 'auto', md: '7px' },
        transform: { xs: 'translateX(-50%)', md: 'none' },
        zIndex: 1000,
        display: 'flex',
        flexDirection: { xs: 'row', md: 'column' },
        backgroundColor: 'rgba(0, 0, 0, 0.2)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        border: '1px solid rgba(255, 255, 255, 0.05)',
        borderRadius: '25px',
        p: { xs: showIcons ? 2 : 0, md: '8px 4px' },
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
        opacity: { xs: showIcons ? 1 : 0, md: 1 },
        visibility: { xs: showIcons ? 'visible' : 'hidden', md: 'visible' },
        transition: 'all 0.3s ease-in-out',
        maxHeight: { xs: showIcons ? '100px' : '0', md: 'none' },
        overflow: 'hidden',
        alignItems: 'center',
        gap: { xs: 2, md: 3 },
        cursor: 'pointer',
        pointerEvents: 'auto',
        width: { xs: 'auto', md: '42px' },
        boxSizing: 'border-box',
        '& > *': {
          flexShrink: 0,
          margin: '0 !important',
        }
      }}>
        <ZoomInButton mapRef={mapRef} />
        <ZoomOutButton mapRef={mapRef} />
        <FullScreenButton />
      </Box>

      <Box sx={{
        position: 'absolute',
        bottom: { xs: showIcons ? '150px' : '80px', md: 'auto' },
        left: { xs: '50%', md: 'auto' },
        right: { xs: 'auto', md: '20px' },
        transform: 'translateX(-50%)',
        zIndex: 1001,
        display: { xs: 'flex', md: 'none' },
        justifyContent: 'center',
        alignItems: 'center',
        transition: 'bottom 0.3s ease-in-out',
        '&:hover': {
          '& .MuiIconButton-root': {
            transform: 'scale(1.1)'
          }
        }
      }}>
        <IconButton
          onClick={() => setShowIcons(!showIcons)}
          sx={{
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            color: 'white',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              transform: 'scale(1.1)'
            }
          }}
        >
          {showIcons ? <ArrowDownwardIcon /> : <ArrowUpwardIcon />}
        </IconButton>
      </Box>
      {isCNNLayer && (
        <Box
          sx={{
            position: 'absolute',
            right: 9.5,
            bottom: { xs: 140, sm: 105 },
            zIndex: 1001,
            display: 'flex',
            alignItems: 'center',
            transition: 'all 0.3s ease-in-out',
          }}
        >
          <Box
            sx={{
              backgroundColor: 'rgba(211, 47, 47, 0.8)',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              color: 'white',
              p: '6px 12px',
              borderTopLeftRadius: '20px',
              borderBottomLeftRadius: '20px',
              display: { xs: 'flex', sm: 'flex' },
              flexDirection: 'column',
              justifyContent: 'center',
              textAlign: 'left',
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: '0.8rem', lineHeight: 1.1 }}>
              {selectedCategory[0] === 'Surface' ? 'SURFACE' : 'ABOVE'}
            </Typography>
            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
              {selectedCategory[0] === 'Surface' ? '' : `FL ${selectedCategory[0].replace(/\D/g, '')}`}
            </Typography>
          </Box>
          <IconButton
            onClick={() => setShowVerticalSlider(!showVerticalSlider)}
            sx={{
              backgroundColor: showVerticalSlider ? "rgba(56, 142, 60, 0.85)" : "rgba(76, 175, 80, 0.8)",
              backdropFilter: 'blur(16px)',
              color: "white",
              borderRadius: '50%',
              width: { xs: 48, md: 56 },
              height: { xs: 48, md: 56 },
              border: '1px solid rgba(255, 255, 255, 0.25)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              "&:hover": {
                backgroundColor: "rgba(46, 125, 50, 0.95)",
                transform: 'scale(1.1)',
                boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
                borderColor: 'rgba(255, 255, 255, 0.4)'
              },
              transition: "all 0.3s ease-in-out",
            }}
          >
            <FlightTakeoffIcon sx={{ fontSize: { xs: 24, md: 28 } }} />
          </IconButton>
        </Box>
      )}
      <Box sx={{
        position: 'absolute',
        bottom: '0',
        left: '0',
        right: '0',
        minHeight: '80px',
        backgroundColor: '#161616',
        zIndex: 1000,
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'center',
        alignItems: 'center',
        p: { xs: 1, md: '0 20px' },
        backgroundColor: '#161616',
        boxSizing: 'border-box',
        gap: { xs: 0, md: 0 }
      }}>
        <Box sx={{
          width: '100%',
          justifyContent: 'center',
          order: { xs: 1, md: 1 },
          marginLeft: { xs: 0, md: '30%', lg: '20%' }
        }}>
          {activeLayerName === 'Convection' && (
            <TimeSlider
              onTimeChange={onTimeChange}
              forecastValue={forecastValue}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              fetchGeoJSON={fetchGeoJSON}
              formatForecastDate={formatForecastDate}
            />
          )}
          {activeLayerName === 'CNN' && (
            <TimeSlider
              onTimeChange={onTimeChange}
              forecastValue={forecastValue}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              fetchGeoJSON={fetchGeoJSON}
              formatForecastDate={formatForecastDate}
            />
          )}
          {activeLayerName === 'Visibility' && (
            <TimeSliderVisibility
              onTimeChange={onTimeChange}
              forecastValue={forecastValue}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              fetchGeoJSON={fetchGeoJSON}
              formatForecastDate={formatForecastDate}
            />
          )}
          {activeLayerName === 'Nowcasting' && (
            <TimeSliderNowcasting
              onTimeChange={onTimeChange}
              forecastValue={forecastValue}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              fetchGeoJSON={fetchGeoJSON}
              formatForecastDate={formatForecastDate}
            />
          )}
          {activeLayerName === 'Ice' && (
            <TimeSlider
              onTimeChange={onTimeChange}
              forecastValue={forecastValue}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              fetchGeoJSON={fetchGeoJSON}
              formatForecastDate={formatForecastDate}
            />
          )}
          {activeLayerName === 'Turbulence' && (
            <TimeSlider
              onTimeChange={onTimeChange}
              forecastValue={forecastValue}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              fetchGeoJSON={fetchGeoJSON}
              formatForecastDate={formatForecastDate}
            />
          )}
          {activeLayerName === 'Dust' && (
            <TimeSliderDust
              onTimeChange={onTimeChange}
              forecastValue={forecastValue}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              fetchGeoJSON={fetchGeoJSON}
              formatForecastDate={formatForecastDate}
            />
          )}
        </Box>

        <Box sx={{
          width: '100%',
          display: 'flex',
          justifyContent: { xs: 'flex-end', md: 'flex-end' },
          order: { xs: 2, md: 2 },
          mt: { xs: 0, md: 0 },
          mb: { xs: 0, md: 0 }
        }}>
          <Button
            component="a"
            onClick={() => navigate('/forecast-metrics')}
            startIcon={<QueryStatsIcon />}
            variant="contained"
            aria-label={!showText ? 'Forecast metrics' : undefined}
            sx={{
              textTransform: 'none',
              fontWeight: 'bold',
              fontSize: { xs: '0.72rem', md: '0.75rem' },
              margin: 0,
              whiteSpace: 'nowrap',


              minWidth: { xs: 44, md: 140 },
              height: { xs: 36, md: 'auto' },
              borderRadius: 2,
              boxShadow: 3,
              px: { xs: 1.2, md: 2 },
              py: { xs: 0.6, md: 1 },

              color: '#fff',
              backgroundColor: 'rgba(35, 155, 105, 0.8)',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                backgroundColor: 'rgba(28, 124, 84, 0.95)',
                boxShadow: '0 12px 40px rgba(35, 155, 105, 0.4)',
                transform: 'translateY(-2px)',
                borderColor: 'rgba(255, 255, 255, 0.4)'
              },
              '& .MuiButton-startIcon': { mr: showText ? 0.7 : 0 },
              '& .MuiButton-startIcon svg': { fontSize: { xs: 20, md: 20 } },
            }}
          >
            {showText && 'Forecast Metrics'}
          </Button>
        </Box>
      </Box>

    </div>
  );

}

export default ViewMap;